import { Modal } from "@msys/ui";
import { ButtonProps } from "@mui/material";
import { DialogContentText } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { AddressInput as AddressInputGQLType } from "../../../clients/graphqlTypes.js";
import { AddressDetails__AddressFragment } from "./Addresses.generated.js";
import { AddressInput } from "./AddressInput.js";
import { getAddressInputFromAddress } from "./getAddressInputFromAddress.js";

export interface GetAddressModalProps {
  id?: string;
  inputLabel: string;
  value?: AddressDetails__AddressFragment | null;
  title?: string;
  description?: string;
  handleClose: () => void;
  handleComplete: (
    address: AddressInputGQLType,
    handleClose: () => void
  ) => Promise<void>;
  allowCustom?: boolean;
  hideCancel?: boolean;
  secondaryAction?: {
    label: string;
    buttonProps?: ButtonProps;
    handleAction: (handleClose: () => void) => void | Promise<void>;
  };
  extraContent?: React.ReactNode;
}

export const GetAddressModal = ({
  id,
  inputLabel,
  value,
  title,
  description,
  handleClose,
  handleComplete,
  allowCustom = false,
  hideCancel = false,
  secondaryAction,
  extraContent,
}: GetAddressModalProps) => {
  const { t } = useTranslate(["Address", "Global"]);
  const [address, setAddress] = React.useState<AddressInputGQLType | null>(
    value ? getAddressInputFromAddress(value) : null
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const hasSecondaryAction = !!secondaryAction;
  const showCancel = !hideCancel && !hasSecondaryAction;

  return (
    <Modal
      id={id}
      title={title ?? t("Address", { ns: "Address" })}
      handleClose={handleClose}
      actionButtons={[
        ...(showCancel
          ? [
              {
                label: t("Cancel", { ns: "Global" }),
                handleClick: handleClose,
                buttonProps: { variant: "text" as const },
              },
            ]
          : []),
        ...(secondaryAction
          ? [
              {
                label: secondaryAction.label,
                buttonProps: {
                  variant: "text" as const,
                  ...secondaryAction.buttonProps,
                  onClick: async () => {
                    await secondaryAction.handleAction(handleClose);
                  },
                },
              },
            ]
          : []),
        {
          label: t("Save", { ns: "Global" }),
          buttonProps: {
            disabled: !address,
            onClick: async () => {
              if (!address) return;
              setIsSubmitting(true);
              await handleComplete(address, handleClose);
              setIsSubmitting(false);
            },
            loading: isSubmitting,
          },
        },
      ]}
    >
      {description && <DialogContentText>{description}</DialogContentText>}
      <AddressInput
        label={inputLabel}
        // options={options}
        value={address}
        onChange={value => {
          setAddress(value);
        }}
        allowCustom={allowCustom}
      />
      {extraContent}
    </Modal>
  );
};
